import { CommonUtility } from '@utility/common';
import { tooltipTexts } from '@utility/constants/common';
import { z } from 'zod';
import type { CartonAttribute, Measure } from './inventory';

/* One carton alias of an item at this provider: added, or its set-up edited. */

export const CARTON_MEASURES = [
  'carton_length',
  'carton_width',
  'carton_height',
  'carton_gross_weight',
  'carton_net_weight',
] as const;

export type CartonMeasureKey = (typeof CARTON_MEASURES)[number];

const isWeight = (key: CartonMeasureKey) =>
  key === 'carton_gross_weight' || key === 'carton_net_weight';

/** Every side, both weights, the alias and the units per carton are set. */
export function isCompleteCarton(attribute?: CartonAttribute) {
  return (
    !!attribute &&
    CARTON_MEASURES.every(key => !!attribute[key]?.value) &&
    !!attribute.alias &&
    !!attribute.units_per_carton
  );
}

const round2 = (value: number) => Math.round(value * 100) / 100;

function convertMeasure(measure: Measure | undefined, toImperial: boolean) {
  if (!measure) return measure;

  const value = Number(measure.value) || 0;

  if (toImperial) {
    if (measure.unit === 'cm') {
      return { unit: 'inch', value: CommonUtility.centiToInch(value) };
    }
    if (measure.unit === 'kg') {
      return { unit: 'lb', value: CommonUtility.kiloToPound(value) };
    }
  } else {
    if (measure.unit === 'inch' || measure.unit === 'in') {
      return { unit: 'cm', value: CommonUtility.inchToCenti(value) };
    }
    if (measure.unit === 'lb') {
      return { unit: 'kg', value: CommonUtility.poundToKilo(value) };
    }
  }

  return measure;
}

/** The carton's sides and weights in inches and pounds, or in cm and kg. */
export function convertCarton(
  attribute: CartonAttribute,
  toImperial: boolean,
): CartonAttribute {
  const converted = { ...attribute };
  for (const key of CARTON_MEASURES) {
    converted[key] = convertMeasure(attribute[key], toImperial);
  }

  return converted;
}

export type CartonAliasValues = {
  alias: string;
  units_per_carton: number | null;
  available_qty?: number | null;
} & Record<CartonMeasureKey, number | null>;

const required = (label: string) =>
  z.number({
    required_error: `${label} is required field`,
    invalid_type_error: `${label} is required field`,
  });

const positive = (label: string) =>
  required(label).gt(0, `${label} must be greater than 0`);

/** The alias, units and every side and weight; cartons on hand for a manual provider. */
export function cartonAliasSchema(requireAvailable: boolean) {
  return z
    .object({
      alias: z.string().trim().min(1, 'Carton Alias Name is required field'),
      units_per_carton: positive('Units per carton').max(20000000),
      carton_length: positive('Carton length'),
      carton_width: positive('Carton width'),
      carton_height: positive('Carton height'),
      carton_gross_weight: positive('Carton gross weight'),
      carton_net_weight: positive('Carton net weight'),
      available_qty: requireAvailable
        ? required('Available Quantity')
        : z.number().nullable().optional(),
    })
    .superRefine((values, ctx) => {
      if (values.carton_net_weight > values.carton_gross_weight) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['carton_net_weight'],
          message: 'Should be less than Gross Weight',
        });
      }
    });
}

/** The form's values for a carton already in the form's unit system. */
export function toCartonForm(
  attribute: CartonAttribute | undefined,
  availableCartons?: number | null,
): CartonAliasValues {
  const values = {
    alias: attribute?.alias ?? '',
    units_per_carton: attribute?.units_per_carton ?? null,
    available_qty: availableCartons ?? null,
  } as CartonAliasValues;

  for (const key of CARTON_MEASURES) {
    const value = attribute?.[key]?.value;
    values[key] = value === undefined || value === '' ? null : Number(value);
  }

  return values;
}

/** The same sides and weights after the unit system flips; the rest stays. */
export function convertCartonForm(
  values: CartonAliasValues,
  toImperial: boolean,
): CartonAliasValues {
  const converted = { ...values };

  for (const key of CARTON_MEASURES) {
    const value = values[key];
    if (value === null || value === undefined) continue;

    if (isWeight(key)) {
      converted[key] = round2(
        toImperial
          ? CommonUtility.kiloToPound(value)
          : CommonUtility.poundToKilo(value),
      );
    } else {
      converted[key] = round2(
        toImperial
          ? CommonUtility.centiToInch(value)
          : CommonUtility.inchToCenti(value),
      );
    }
  }

  return converted;
}

/**
 * The inventory update for one alias, sides and weights sent in cm and kg.
 * `availableCartons` is left out where the provider reports stock itself.
 */
export function buildCartonUpdate(
  values: CartonAliasValues,
  {
    itemId,
    imperial,
    availableCartons,
  }: { itemId: string; imperial: boolean; availableCartons?: number | null },
) {
  const unitsPerCarton = values.units_per_carton ?? 0;
  const attributes: CartonAttribute = { units_per_carton: unitsPerCarton };

  for (const key of CARTON_MEASURES) {
    const unit = isWeight(key)
      ? imperial
        ? 'lb'
        : 'kg'
      : imperial
        ? 'inch'
        : 'cm';
    attributes[key] = convertMeasure({ unit, value: values[key] ?? '' }, false);
  }

  const variant: {
    alias: string;
    attributes: CartonAttribute;
    available?: number;
  } = { alias: values.alias.trim(), attributes };

  if (availableCartons !== undefined) {
    variant.available = (Number(availableCartons) || 0) * unitsPerCarton || 0;
  }

  return { inventories: [{ variants: [variant], item_id: itemId }] };
}

/** Field labels with the unit in brackets, and help texts that name it. */
export function cartonFieldLabels(imperial: boolean) {
  const length = imperial ? 'in.' : 'cm';
  const weight = imperial ? 'lb' : 'kg';
  const lengthHelp = imperial ? 'inches' : 'cm';

  return {
    carton_gross_weight: {
      label: `Gross Weight (${weight})`,
      hint: `${tooltipTexts.cartonGrossWeight} ${weight}.`,
    },
    carton_net_weight: {
      label: `Net Weight (${weight})`,
      hint: `${tooltipTexts.cartonNetWeight} ${weight}.`,
    },
    carton_length: {
      label: `Carton Length (${length})`,
      hint: `${tooltipTexts.cartonLength} ${lengthHelp}`,
    },
    carton_width: {
      label: `Carton Width (${length})`,
      hint: `${tooltipTexts.cartonWidth} ${lengthHelp}`,
    },
    carton_height: {
      label: `Carton Height (${length})`,
      hint: `${tooltipTexts.cartonHeight} ${lengthHelp}`,
    },
  } satisfies Record<CartonMeasureKey, { label: string; hint: string }>;
}
